import { Injectable } from '@angular/core';
import { DispatchedServiceViewModel } from '../../models/view-models/dispatched-service.view-model';
import { UUID } from 'angular2-uuid';
import { map, Observable } from 'rxjs';
import { GetUserServicesQueryService } from '../queries/get-user-services-query.service';

/**
 * Service for getting ordered dispatched services.
 */
@Injectable({
	providedIn: 'root'
})
export class OrderedServicesService {
	/**
	 * @constructor
	 *
	 * @param getUserServicesQuery The query service used to retrieve all services associated with the current user.
	 */
	constructor(
		private readonly getUserServicesQuery: GetUserServicesQueryService
	) { }

	/**
	 * Retrieves dispatched services in the order defined by the service state.
	 * The service state represents metadata associated with each dispatched service.
	 * @returns An Observable emitting an ordered list of dispatched service view models.
	 */
	public GetOrderedServices$(): Observable<DispatchedServiceViewModel[]> {
		return this.getUserServicesQuery.GetServices$().pipe(
			map((services: DispatchedServiceViewModel[]) => {
				return services.sort((a: DispatchedServiceViewModel, b: DispatchedServiceViewModel) => {
					// Primary sort: Sequence ascending (least to greatest)
					const sequenceDiff: number = a.Sequence - b.Sequence;
					if (sequenceDiff !== 0) {
						return sequenceDiff;
					}
					
					// Secondary sort: Retrieved Date ascending
					const aDate: number = a.RetrievedDate ? new Date(a.RetrievedDate).getTime() : 0;
					const bDate: number = b.RetrievedDate ? new Date(b.RetrievedDate).getTime() : 0;
					return bDate - aDate;
				});
			})
		);
	}

	/**
	 * Gets a specific dispatched service by its service ID.
	 * @param serviceId The ID of the dispatched service to retrieve.
	 * @returns An observable emitting the dispatched service view model matching the given ID.
	 */
	public GetOrderedServiceById$(serviceId: UUID): Observable<DispatchedServiceViewModel> {
		return this.GetOrderedServices$().pipe(
			map((services: DispatchedServiceViewModel[]) => services.find((service: DispatchedServiceViewModel) => service.ServiceId == serviceId)!)
		);
	}

	/**
	 * Gets the list of dispatched services that have been accepted.
	 * @returns The list of dispatched services that have been accepted.
	 */
	public GetAcceptedServices$(): Observable<DispatchedServiceViewModel[]> {
		return this.GetOrderedServices$().pipe(
			map((services: DispatchedServiceViewModel[]) => {
				return services.filter((service: DispatchedServiceViewModel) => service.HasDriverAccepted);
			})
		);
	}

	/**
	 * Gets the list of accepted dispatched services that have not been performed in order.
	 * @returns An observable of the list of accepted dispatched services that have not been performed in order.
	 */
	public GetAcceptedAndNotPerformedServices$(): Observable<DispatchedServiceViewModel[]> {
		return this.GetOrderedServices$().pipe(
			map((services: DispatchedServiceViewModel[]) => {
				return services.filter((service: DispatchedServiceViewModel) => service.IsAcceptedAndNotPerformed);
			})
		);
	}
}
